import { grinsViewQueryOptions } from '@/api/registrationQueryOptions';
import { ApiErrorComponent } from '@/components/ApiErrorComponent';
import { useColumnState } from '@/components/hooks/useColumnState';
import useGridPageSize from '@/components/hooks/useGridPageSize';
import { usePersistedVisibilityModel } from '@/components/hooks/usePersistedVisibilityModel';
import useResponsive from '@/components/hooks/useResponsive';
import LoadProgress from '@/components/LoadProgress';
import { DetailGridToolbar } from '@/components/mui-x/datagrid/DetailGridToolbar';
import { Box } from '@mui/material';
import { GridColDef, GridColumnVisibilityModel, GridLogicOperator } from '@mui/x-data-grid';
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium';
import { deDE } from '@mui/x-data-grid-premium/locales';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import React from 'react';

export const Route = createFileRoute('/_auth/grinsView')({
    component: GrinsViewComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

function GrinsViewComponent() {

    // const sessionOrganization = useSelector(getOrganization);

    const grinsViewQuery = useSuspenseQuery(grinsViewQueryOptions())
    const grinsView = grinsViewQuery.data

    // const permissionsQuery = useSuspenseQuery(fetchDetailPermissionsQueryOptions('organization', sessionOrganization.uuid, ['RegistrationTransfer']))
    // const permissions = permissionsQuery.data

    // const navigate = useNavigate();
    const { xsmall, small, medium, large, xlarge } = useResponsive();

    const columns: GridColDef[] = [];
    columns.push({
        field: 'personenkey',
        headerName: 'Mitgliedsnummer',
        valueGetter: (_value, row) => `${row.grinsRecord.personenkey || ''}`,
    }, {
        field: 'gruppenschluessel',
        headerName: 'Gruppenschlüssel',
        valueGetter: (_value, row) => `${row.grinsRecord.gruppenschluessel || ''}`,
    }, {
        field: 'titel',
        headerName: 'Titel',
        valueGetter: (_value, row) => `${row.grinsRecord.titel || ''}`,
    }, {
        field: 'name',
        headerName: 'Name',
        valueGetter: (_value, row) => `${row.grinsRecord.name || ''}`,
    }, {
        field: 'vorname',
        headerName: 'Vorname',
        valueGetter: (_value, row) => `${row.grinsRecord.vorname || ''}`,
    }, {
        field: 'anrede',
        headerName: 'Anrede',
        valueGetter: (_value, row) => `${row.grinsRecord.anrede || ''}`,
    }, {
        field: 'gebTag',
        headerName: 'GebTag',
        valueGetter: (_value, row) => `${row.grinsRecord.gebtag || ''}`,
    }, {
        field: 'gebMonat',
        headerName: 'GebMonat',
        valueGetter: (_value, row) => `${row.grinsRecord.gebmonat || ''}`,
    }, {
        field: 'gebJahr',
        headerName: 'GebJahr',
        valueGetter: (_value, row) => `${row.grinsRecord.gebjahr || ''}`,
    }, {
        field: 'strasse',
        headerName: 'Straße',
        valueGetter: (_value, row) => `${row.grinsRecord.strasse || ''}`,
    }, {
        field: 'plz',
        headerName: 'PLZ',
        valueGetter: (_value, row) => `${row.grinsRecord.plz || ''}`,
    }, {
        field: 'ort',
        headerName: 'Ort',
        valueGetter: (_value, row) => `${row.grinsRecord.ort || ''}`,
    }, {
        field: 'geschlecht',
        headerName: 'Geschlecht',
        valueGetter: (_value, row) => `${row.grinsRecord.geschlecht || ''}`,
    }, {
        field: 'aktiv',
        headerName: 'Aktiv',
        valueGetter: (_value, row) => `${row.grinsRecord.aktiv || ''}`,
    }, {
        field: 'ermaessigt',
        headerName: 'Ermäßigt',
        valueGetter: (_value, row) => `${row.grinsRecord.ermaessigt || ''}`,
    }, {
        field: 'email',
        headerName: 'Email',
        valueGetter: (_value, row) => `${row.grinsRecord.email || ''}`,
    }, {
        field: 'religion',
        headerName: 'Religion',
        valueGetter: (_value, row) => `${row.grinsRecord.religion || ''}`,
    }, {
        field: 'telefon',
        headerName: 'Telefon',
        valueGetter: (_value, row) => `${row.grinsRecord.telefon || ''}`,
    }, {
        field: 'funktionen',
        headerName: 'Funktionen',
        valueGetter: (_value, row) => `${row.grinsRecord.funktionen || ''}`,
    }
    )

    const initialVisibilityModel = {
        'member': xsmall || small || medium,
        'firstname': large || xlarge,
        'lastname': large || xlarge,
        'scoutId': large || xlarge,
    } as GridColumnVisibilityModel

    const [visibilityModel, setVisibilityModel] = usePersistedVisibilityModel('grinsView', initialVisibilityModel)
    const handleVisibilityModelChange = React.useCallback((model: GridColumnVisibilityModel) => {
        setVisibilityModel(model)
    }, [setVisibilityModel]);

    // const responsive = useResponsive();

    const defaultLocaleText = deDE.components.MuiDataGrid.defaultProps.localeText
    const localeText = { ...defaultLocaleText, ...{ toolbarExportLabel: 'Export' } }

    const gridPageSize = useGridPageSize()
    const pageSizeOptions = gridPageSize.getPageSizeOptions('grinsView', 35)
    const [paginationModel, setPaginationModel] = React.useState(gridPageSize.getPaginationModel('grinsView', 35));

    const CustomToolbar = () => {
        return (
            <DetailGridToolbar scope={'grinsView'} getScopeName={() => 'grinsView'} />
        )
    }

    const apiRef = useGridApiRef()
    const columnsState = useColumnState('grinsView', apiRef, columns);

    if (grinsViewQuery.isLoading || grinsViewQuery.isFetching || !grinsView) {
        return <LoadProgress />
    }

    return (
        <Box sx={{
            height: { xs: `calc(100% - 50px)` }
        }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <DataGridPremium
                    columns={columnsState.columns}
                    onColumnWidthChange={columnsState.onColumnWidthChange}
                    onColumnOrderChange={columnsState.onColumnOrderChange}
                    rows={grinsView}
                    getRowId={(row) => { return row.uuid || '' }}
                    localeText={localeText}
                    pagination
                    pageSizeOptions={pageSizeOptions}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model) => {
                        setPaginationModel(model)
                        gridPageSize.handlePaginationModelChange('awards', model)
                    }}
                    slots={{ toolbar: CustomToolbar }}
                    slotProps={{
                        filterPanel: {
                            logicOperators: [GridLogicOperator.And],
                            sx: { maxWidth: '100vw' }
                        },
                    }}
                    density='compact'
                    columnVisibilityModel={visibilityModel}
                    onColumnVisibilityModelChange={handleVisibilityModelChange}
                    hideFooter={true}
                    disableDensitySelector
                    disableAggregation
                    disableColumnPinning
                    disableColumnReorder
                    disableRowGrouping
                    disablePivoting
                    showToolbar />
            </div>
        </Box>
    );
}
